import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Dropdown from "../../common/dropDown";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useUserData } from "../../../hooks/useUserData";
import { fetchQmsDashboard } from "./qmsService";
import {
  buildAtRiskFeed,
  buildDetailRows,
  buildQmsDashboardModel,
  DEFAULT_QMS_DASHBOARD_RANGE,
  formatDashboardDateLabel,
  getBreakdownList,
  QMS_CC_TABS,
  QMS_DATE_PRESETS,
  resolveQmsDashboardDateRange,
} from "./qmsDashboardUtils";
import {
  CC_COLORS,
  readinessCssVar,
  readinessHex,
} from "../isf/commandCenterModel";
import "../isf/IsfCommandCenter.scss";

function Gauge({ percent, label, size = 150 }) {
  const p = Math.min(100, Math.max(0, Number(percent) || 0));
  const R = size * 0.36;
  const cx = size / 2;
  const cy = size / 2;
  const circ = 2 * Math.PI * R;
  const off = circ * (1 - p / 100);
  const stroke = readinessHex(p);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      <circle cx={cx} cy={cy} r={R} fill="none" stroke="#eef2f1" strokeWidth={size * 0.08} />
      <circle
        cx={cx}
        cy={cy}
        r={R}
        fill="none"
        stroke={stroke}
        strokeWidth={size * 0.08}
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={off}
        transform={`rotate(-90 ${cx} ${cy})`}
      />
      <text x={cx} y={cy + size * 0.02} textAnchor="middle" fontSize={size * 0.24} fontWeight="800" fill={stroke}>
        {p}%
      </text>
      {label ? (
        <text x={cx} y={cy + size * 0.2} textAnchor="middle" fontSize={size * 0.09} fill="#6a7a77">
          {label}
        </text>
      ) : null}
    </svg>
  );
}

function KpiCard({ title, value, detail, color, barColor, filterKey, selected = false, onClick }) {
  return (
    <div
      className={`isf-cc__kpi${selected ? " sel" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onClick(filterKey)}
      onKeyDown={(e) => e.key === "Enter" && onClick(filterKey)}
    >
      <span className="isf-cc__kbar" style={{ background: barColor || "var(--cc-line)" }} />
      <div className="isf-cc__kt">{title}</div>
      <div className="isf-cc__kv" style={{ color: color || "var(--cc-ink)" }}>
        {value}
      </div>
      {detail ? <div className="isf-cc__kd" dangerouslySetInnerHTML={{ __html: detail }} /> : null}
    </div>
  );
}

function StatusBadge({ read, unread }) {
  if (unread > 0 && read === 0) {
    return <span className="isf-cc__badge isf-cc__badge--ovd">Pending</span>;
  }
  if (unread > 0) {
    return <span className="isf-cc__badge isf-cc__badge--pen">In progress</span>;
  }
  if (read > 0) {
    return <span className="isf-cc__badge isf-cc__badge--ful">Complete</span>;
  }
  return <span className="isf-cc__badge isf-cc__badge--na">N/A</span>;
}

export default function QmsCommandCenter() {
  const navigate = useNavigate();
  const { userData } = useUserData();
  const loadRef = useRef(0);

  const [datePreset, setDatePreset] = useState({
    label: "Last 7 days",
    value: DEFAULT_QMS_DASHBOARD_RANGE.preset,
  });
  const [dimension, setDimension] = useState({ label: "All dimensions", value: "all" });
  const [tab, setTab] = useState("C");
  const [mmode, setMmode] = useState("read");
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [model, setModel] = useState(null);
  const [asOf, setAsOf] = useState(() => new Date());

  const resolvedRange = useMemo(
    () => resolveQmsDashboardDateRange({ preset: datePreset.value }),
    [datePreset.value],
  );

  const loadCenter = useCallback(async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    const runId = ++loadRef.current;
    setLoading(true);
    setLoadError("");
    try {
      const response = await fetchQmsDashboard(userData, resolvedRange);
      if (runId !== loadRef.current) return;
      setModel(buildQmsDashboardModel(response));
      setAsOf(new Date());
    } catch (err) {
      if (runId !== loadRef.current) return;
      console.error(err);
      setLoadError("Dashboard data could not be loaded.");
      setModel(null);
    } finally {
      if (runId === loadRef.current) setLoading(false);
    }
  }, [userData, resolvedRange]);

  useEffect(() => {
    loadCenter();
  }, [loadCenter]);

  const summary = model?.summary || {
    read: 0,
    unread: 0,
    total: 0,
    completionRate: 0,
    activeDays: 0,
    rangeDays: 0,
    typesWithPending: 0,
    rolesWithPending: 0,
  };

  const atRiskFeed = useMemo(() => buildAtRiskFeed(model), [model]);
  const detailRows = useMemo(
    () => buildDetailRows(model, { dimension: dimension.value, sel }),
    [model, dimension.value, sel],
  );

  const toggleSel = (key) => {
    if (!key) {
      setSel(null);
      return;
    }
    setSel((prev) => (prev?.k === key ? null : { k: key }));
  };

  const onBreakdownClick = (row) => {
    setSel((prev) =>
      prev?.label === row.label ? null : { label: row.label, dimension: row.dimension },
    );
  };

  const renderBreakdownBars = (rows, emptyNote) => {
    if (!rows.length) return <p className="isf-cc__note">{emptyNote}</p>;
    return (
      <>
        {rows.slice(0, 8).map((row) => {
          const total = row.total || row.read + row.unread;
          if (!total) return null;
          return (
            <div
              key={row.label}
              className="isf-cc__bar"
              role="button"
              tabIndex={0}
              onClick={() => onBreakdownClick(row)}
            >
              <div className="lab">{row.label}</div>
              <div className="track">
                <span
                  className="isf-cc__seg isf-cc__seg--ful"
                  style={{ width: `${(100 * row.read) / total}%` }}
                />
                <span
                  className="isf-cc__seg isf-cc__seg--await"
                  style={{ width: `${(100 * row.unread) / total}%` }}
                />
              </div>
              <div className="val" style={{ color: readinessHex(row.pct) }}>
                {row.pct}%
              </div>
            </div>
          );
        })}
        <div className="isf-cc__legend">
          <span>
            <i style={{ background: "var(--cc-green)" }} /> Completed
          </span>
          <span>
            <i style={{ background: "var(--cc-amber)" }} /> Pending
          </span>
        </div>
      </>
    );
  };

  const renderTimelineMatrix = () => {
    const timeline = model?.timeline || [];
    if (!timeline.length) {
      return <p className="isf-cc__note">No daily activity in this period.</p>;
    }

    const head = (
      <tr>
        <th />
        {timeline.map((day) => (
          <th key={day.dateLabel}>
            <div className="rot">{formatDashboardDateLabel(day.dateLabel)}</div>
          </th>
        ))}
        <th className="mrow">Rate</th>
      </tr>
    );

    const rows = [
      { key: "read", label: "Completed", mode: "fulfilled", getValue: (d) => d.read },
      { key: "unread", label: "Pending", mode: "pending", getValue: (d) => d.unread },
    ].filter((row) => mmode === "both" || mmode === row.key);

    const body = rows.map((row) => {
      const total = timeline.reduce((acc, day) => acc + row.getValue(day), 0);
      const rowTotal = timeline.reduce((acc, day) => acc + day.total, 0);
      const rowPct = rowTotal > 0 ? Math.round((100 * total) / rowTotal) : 0;
      return (
        <tr key={row.key}>
          <th title={row.label}>{row.label}</th>
          {timeline.map((day) => {
            const value = row.getValue(day);
            if (!value) {
              return (
                <td key={day.dateLabel}>
                  <div className="isf-cc__mc faint">·</div>
                </td>
              );
            }
            return (
              <td key={day.dateLabel}>
                <div
                  className={`isf-cc__mc isf-cc__mc--${row.mode}`}
                  role="button"
                  tabIndex={0}
                  title={`${row.label}: ${value}`}
                  onClick={() => setSel({ k: row.key === "read" ? "read" : "unread" })}
                >
                  {value > 99 ? "99+" : value}
                </div>
              </td>
            );
          })}
          <td className="mrow">
            <span style={{ color: readinessHex(rowPct), fontWeight: 800 }}>{rowPct}%</span>
          </td>
        </tr>
      );
    });

    return (
      <table className="isf-cc__mtx">
        <thead>{head}</thead>
        <tbody>{body}</tbody>
      </table>
    );
  };

  const renderDetailTable = () => (
    <table className="isf-cc__tbl">
      <thead>
        <tr>
          <th>Dimension</th>
          <th>Group</th>
          <th>Completed</th>
          <th>Pending</th>
          <th>Rate</th>
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {detailRows.map((row) => (
          <tr key={`${row.dimension}-${row.label}`}>
            <td>{row.dimension}</td>
            <td>
              <b>{row.label}</b>
            </td>
            <td>{row.read}</td>
            <td style={{ color: row.unread ? "var(--cc-red)" : "inherit", fontWeight: row.unread ? 700 : 400 }}>
              {row.unread}
            </td>
            <td style={{ color: readinessHex(row.pct), fontWeight: 700 }}>{row.pct}%</td>
            <td>
              <StatusBadge read={row.read} unread={row.unread} />
            </td>
            <td>
              <button type="button" className="isf-cc__act" onClick={() => onBreakdownClick(row)}>
                Focus
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const filterNote = () => {
    let label = "All learning activity";
    if (sel?.label) label = sel.label;
    else if (sel?.k === "read") label = "Completed only";
    else if (sel?.k === "unread") label = "Pending only";
    else if (sel?.k === "low") label = "Below 70% compliance";
    return (
      <div className="isf-cc__filtnote">
        <button type="button" className="isf-cc__chip" onClick={() => sel && setSel(null)}>
          {dimension.label}
          {sel ? ` • ${label} ✕` : ""}
        </button>
        <span>
          {detailRows.length} row{detailRows.length !== 1 ? "s" : ""}
        </span>
      </div>
    );
  };

  const renderRankedList = (rows, title) => (
    <div className="isf-cc__card">
      <h3>
        {title} <span className="isf-cc__mut">— ranked by compliance</span>
      </h3>
      {rows.length ? (
        rows.map((row) => (
          <div
            key={row.label}
            className="isf-cc__rankrow"
            role="button"
            tabIndex={0}
            onClick={() => onBreakdownClick(row)}
          >
            <div className="rn">{row.label}</div>
            <div className="rt">
              <div className="rf" style={{ width: `${row.pct}%`, background: readinessHex(row.pct) }} />
            </div>
            <div className="rp" style={{ color: readinessHex(row.pct) }}>
              {row.pct}%
              <span className="rmini"> · {row.unread} pending</span>
            </div>
          </div>
        ))
      ) : (
        <p className="isf-cc__note">No data for this period.</p>
      )}
    </div>
  );

  const renderCommandCenter = () => (
    <section className={`isf-cc__view${tab === "C" ? " on" : ""}`}>
      <div className="isf-cc__h">
        <h1>QMS Command Center</h1>
        <span className="isf-cc__sub">
          Learning compliance at a glance — reads, pending assignments, and where to act.
        </span>
      </div>
      <div className="isf-cc__kstrip">
        <KpiCard
          title="Compliance"
          value={`${summary.completionRate}%`}
          detail={`${summary.read} of ${summary.total} completed`}
          color={readinessCssVar(summary.completionRate)}
          barColor={readinessHex(summary.completionRate)}
          filterKey=""
          selected={!sel?.k && !sel?.label}
          onClick={() => setSel(null)}
        />
        <KpiCard
          title="Completed"
          value={String(summary.read)}
          detail="acknowledged &amp; passed"
          color="var(--cc-green)"
          barColor="var(--cc-green)"
          filterKey="read"
          selected={sel?.k === "read"}
          onClick={toggleSel}
        />
        <KpiCard
          title="Pending"
          value={String(summary.unread)}
          detail="awaiting staff action"
          color="var(--cc-red)"
          barColor="var(--cc-red)"
          filterKey="unread"
          selected={sel?.k === "unread"}
          onClick={toggleSel}
        />
        <KpiCard
          title="Active days"
          value={String(summary.activeDays)}
          detail={`of ${summary.rangeDays} in range`}
          color="var(--cc-blue)"
          barColor="var(--cc-blue)"
          filterKey=""
          onClick={() => {}}
        />
        <KpiCard
          title="Types pending"
          value={String(summary.typesWithPending)}
          detail="document types with backlog"
          color="var(--cc-amber)"
          barColor="var(--cc-amber)"
          filterKey="unread"
          selected={sel?.k === "unread"}
          onClick={toggleSel}
        />
        <KpiCard
          title="Roles pending"
          value={String(summary.rolesWithPending)}
          detail="roles with open reads"
          color="var(--cc-purple)"
          barColor="var(--cc-purple)"
          filterKey="low"
          selected={sel?.k === "low"}
          onClick={toggleSel}
        />
      </div>

      <div className="isf-cc__card">
        <h3>
          Daily activity
          <span className="isf-cc__mut"> — completed × pending by date</span>
          <span className="isf-cc__toggle">
            <button type="button" className={mmode === "read" ? "on" : ""} onClick={() => setMmode("read")}>
              Completed
            </button>
            <button type="button" className={mmode === "unread" ? "on" : ""} onClick={() => setMmode("unread")}>
              Pending
            </button>
          </span>
        </h3>
        <div style={{ overflow: "auto" }}>{renderTimelineMatrix()}</div>
        <div className="isf-cc__legend">
          <span>
            <i style={{ background: CC_COLORS.fulfilled }} /> Completed
          </span>
          <span>
            <i style={{ background: CC_COLORS.pending }} /> Pending
          </span>
        </div>
      </div>

      <div className="isf-cc__two">
        <div className="isf-cc__card">
          <h3>
            Compliance gauge <span className="isf-cc__mut">— overall period</span>
          </h3>
          <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
            <div className="isf-cc__gauge">
              <Gauge percent={summary.completionRate} label="complete" size={132} />
            </div>
            <div style={{ flex: 1 }}>
              {renderBreakdownBars(model?.byRole || [], "No staff role breakdown for this period.")}
            </div>
          </div>
          <p className="isf-cc__note">
            {summary.unread} pending read{summary.unread !== 1 ? "s" : ""} across the selected period.
          </p>
        </div>
        <div className="isf-cc__card">
          <h3>
            Activity forecast <span className="isf-cc__mut">— volume by dimension</span>
          </h3>
          <div className="isf-cc__fc">
            {[
              ["Document types", summary.typesWithPending, "var(--cc-amber)"],
              ["Categories", (model?.byCategory || []).filter((r) => r.unread > 0).length, "#C9A227"],
              ["Staff roles", summary.rolesWithPending, "var(--cc-purple)"],
              ["Total pending", summary.unread, "var(--cc-red)"],
            ].map(([label, count, col]) => (
              <div
                key={label}
                className="isf-cc__fcb"
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (label === "Document types") setTab("1");
                  else if (label === "Categories") setTab("2");
                  else if (label === "Staff roles") setTab("4");
                  else setSel({ k: "unread" });
                }}
              >
                <div className="fct">{label}</div>
                <div className="fcv" style={{ color: col }}>
                  {count || 0}
                </div>
                <div className="fcm">{label === "Total pending" ? "open reads" : "with backlog"}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="isf-cc__two">
        <div className="isf-cc__card">
          <h3>
            Pending feed <span className="isf-cc__mut">— highest backlog first</span>
          </h3>
          <div className="isf-cc__feed">
            {atRiskFeed.length ? (
              atRiskFeed.map((row) => (
                <div
                  key={`${row.dimension}-${row.label}`}
                  className="isf-cc__fi"
                  role="button"
                  tabIndex={0}
                  onClick={() => onBreakdownClick(row)}
                >
                  <div className="isf-cc__fic" style={{ background: CC_COLORS.await }}>
                    {row.unread > 99 ? "99+" : row.unread}
                  </div>
                  <div>
                    <div className="isf-cc__ft">
                      {row.dimension} · {row.label}
                    </div>
                    <div className="isf-cc__fm">
                      {row.read} completed · {row.pct}% compliance
                    </div>
                  </div>
                  <div
                    className="isf-cc__fr"
                    style={{ background: "var(--cc-amber-bg)", color: "var(--cc-amber)" }}
                  >
                    {row.unread} pending
                  </div>
                </div>
              ))
            ) : (
              <p className="isf-cc__note">No pending items in this period.</p>
            )}
          </div>
        </div>
        <div className="isf-cc__card">
          <h3>Breakdown detail</h3>
          {filterNote()}
          <div style={{ maxHeight: 360, overflow: "auto" }}>{renderDetailTable()}</div>
        </div>
      </div>
    </section>
  );

  const renderBreakdownView = (tabId, title, subtitle, rows) => (
    <section className={`isf-cc__view${tab === tabId ? " on" : ""}`}>
      <div className="isf-cc__h">
        <h1>{title}</h1>
        <span className="isf-cc__sub">{subtitle}</span>
      </div>
      <div className="isf-cc__kstrip isf-cc__kstrip--4">
        <KpiCard
          title="Compliance"
          value={`${summary.completionRate}%`}
          color={readinessCssVar(summary.completionRate)}
          barColor={readinessHex(summary.completionRate)}
          onClick={() => setSel(null)}
        />
        <KpiCard
          title="Completed"
          value={String(summary.read)}
          color="var(--cc-green)"
          barColor="var(--cc-green)"
          filterKey="read"
          selected={sel?.k === "read"}
          onClick={toggleSel}
        />
        <KpiCard
          title="Pending"
          value={String(summary.unread)}
          color="var(--cc-red)"
          barColor="var(--cc-red)"
          filterKey="unread"
          selected={sel?.k === "unread"}
          onClick={toggleSel}
        />
        <KpiCard
          title="Groups tracked"
          value={String(rows.length)}
          onClick={() => {}}
        />
      </div>
      <div className="isf-cc__grid2">
        <div className="isf-cc__card">
          <h3>Overall compliance</h3>
          <div className="isf-cc__gauge">
            <Gauge percent={summary.completionRate} label="complete" size={190} />
            <p className="isf-cc__gauge-cap">
              {summary.read} of {summary.total} reads completed
            </p>
          </div>
        </div>
        <div className="isf-cc__card">
          <h3>
            By group <span className="isf-cc__mut">— completed / pending</span>
          </h3>
          {rows.map((row) => {
            const total = row.total || row.read + row.unread;
            if (!total) return null;
            const seg = (n, kind) =>
              n ? (
                <span
                  className={`isf-cc__seg isf-cc__seg--${kind}`}
                  style={{ width: `${(100 * n) / total}%` }}
                />
              ) : null;
            return (
              <div
                key={row.label}
                className="isf-cc__msbar"
                role="button"
                tabIndex={0}
                onClick={() => onBreakdownClick({ ...row, dimension: title })}
              >
                <div className="code" title={row.label}>
                  {row.label}
                </div>
                <div className="track">
                  {seg(row.read, "ful")}
                  {seg(row.unread, "pen")}
                </div>
                <div className="pct" style={{ color: readinessHex(row.pct) }}>
                  {row.pct}%
                </div>
              </div>
            );
          })}
          <div className="isf-cc__legend" style={{ marginTop: 12 }}>
            <span>
              <i style={{ background: "var(--cc-green)" }} /> Completed
            </span>
            <span>
              <i style={{ background: "var(--cc-blue)" }} /> Pending
            </span>
          </div>
        </div>
      </div>
      <div className="isf-cc__card">
        <h3>Detail table</h3>
        {filterNote()}
        {renderDetailTable()}
      </div>
    </section>
  );

  const snapshotLabel = asOf.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  const rangeLabel = `${formatDashboardDateLabel(resolvedRange.startDate)} – ${formatDashboardDateLabel(resolvedRange.endDate)}`;

  return (
    <div className="isf-cc isf-dashboard qms-command-center">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/dashboard" },
          { label: "QMS", path: "/qms/dashboard" },
          { label: "Compliance Dashboard" },
        ]}
        actions={[{ icon: "fas fa-sync-alt", label: "Refresh", onClick: loadCenter }]}
        toolbarExtras={
          model ? (
            <span className="isf-cc__asof-inline">
              {rangeLabel} · Snapshot as of <b>{snapshotLabel}</b>
            </span>
          ) : null
        }
      />

      {loadError ? <div className="isf-cc__alert">{loadError}</div> : null}

      <div className="isf-cc__wrap">
        <aside className="isf-cc__rail">
          <div className="isf-cc__rl">Filters</div>
          <div className="isf-cc__field">
            <label>Period</label>
            <Dropdown options={QMS_DATE_PRESETS} selected={datePreset} onSelect={setDatePreset} />
          </div>
          <div className="isf-cc__field">
            <label>Focus</label>
            <Dropdown
              options={[
                { label: "All dimensions", value: "all" },
                { label: "Document type", value: "type" },
                { label: "Category", value: "category" },
                { label: "Staff role", value: "role" },
              ]}
              selected={dimension}
              onSelect={(opt) => {
                setDimension(opt);
                setSel(null);
              }}
            />
          </div>
          <div className="isf-cc__rl">Views</div>
          <div className="isf-cc__tabs">
            {QMS_CC_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`isf-cc__tab${t.hero ? " hero" : ""}${tab === t.id ? " on" : ""}`}
                onClick={() => {
                  setTab(t.id);
                  setSel(null);
                }}
              >
                <span className="isf-cc__tab-ic" />
                {t.label}
              </button>
            ))}
          </div>
          {model ? (
            <div className="isf-cc__summary">
              <div className="isf-cc__sr">
                <span>Compliance</span>
                <span style={{ color: readinessHex(summary.completionRate) }}>
                  {summary.completionRate}%
                </span>
              </div>
              <div className="isf-cc__sr">
                <span>Completed</span>
                <span style={{ color: "var(--cc-green)" }}>{summary.read}</span>
              </div>
              <div className="isf-cc__sr">
                <span>Pending</span>
                <span style={{ color: "var(--cc-red)" }}>{summary.unread}</span>
              </div>
              <div className="isf-cc__sr">
                <span>Active days</span>
                <span>{summary.activeDays}</span>
              </div>
              <div className="isf-cc__sr">
                <span>Total activity</span>
                <span>{summary.total}</span>
              </div>
            </div>
          ) : null}
          <div className="isf-cc__rl">Shortcuts</div>
          <div className="isf-cc__tabs">
            <button type="button" className="isf-cc__tab" onClick={() => navigate("/qms/my-learnings")}>
              <span className="isf-cc__tab-ic" />
              My Learnings
            </button>
            <button type="button" className="isf-cc__tab" onClick={() => navigate("/qms/library")}>
              <span className="isf-cc__tab-ic" />
              Document Library
            </button>
            <Link to="/qms/compliance-report" className="isf-cc__tab" style={{ textDecoration: "none" }}>
              <span className="isf-cc__tab-ic" />
              Compliance Report
            </Link>
          </div>
          <p style={{ fontSize: "10.5px", color: "var(--cc-mut)", marginTop: 12 }}>
            Compliance = Completed reads ÷ Total assigned activity in the selected period.
          </p>
        </aside>

        <main className="isf-cc__main">
          {loading ? (
            <div className="isf-cc__loading">
              <div className="spinner-border spinner-border-sm" role="status" />
              <span>Loading dashboard…</span>
            </div>
          ) : null}

          {!model && !loading ? (
            <div className="isf-cc__empty">
              <h2>No dashboard data</h2>
              <p>Try a wider date range or refresh once learning assignments exist.</p>
            </div>
          ) : (
            <>
              {renderCommandCenter()}
              {renderBreakdownView(
                "1",
                "By Document Type",
                "Which document types drive compliance — and where backlog sits.",
                getBreakdownList(model, "1"),
              )}
              {renderBreakdownView(
                "2",
                "By Category",
                "Category-level completion and pending reads.",
                getBreakdownList(model, "2"),
              )}
              <section className={`isf-cc__view${tab === "3" ? " on" : ""}`}>
                <div className="isf-cc__h">
                  <h1>Pending Focus</h1>
                  <span className="isf-cc__sub">Groups with open reads — prioritise follow-up here.</span>
                </div>
                <div className="isf-cc__kstrip isf-cc__kstrip--4">
                  <KpiCard
                    title="Total pending"
                    value={String(summary.unread)}
                    color="var(--cc-red)"
                    barColor="var(--cc-red)"
                    filterKey="unread"
                    selected={sel?.k === "unread"}
                    onClick={toggleSel}
                  />
                  <KpiCard
                    title="Below 70%"
                    value={String(
                      getBreakdownList(model, "3").filter((r) => r.pct < 70 && r.total > 0).length,
                    )}
                    color="var(--cc-amber)"
                    barColor="var(--cc-amber)"
                    filterKey="low"
                    selected={sel?.k === "low"}
                    onClick={toggleSel}
                  />
                  <KpiCard
                    title="Groups affected"
                    value={String(getBreakdownList(model, "3").length)}
                    onClick={() => {}}
                  />
                  <KpiCard
                    title="Compliance"
                    value={`${summary.completionRate}%`}
                    color={readinessCssVar(summary.completionRate)}
                    barColor={readinessHex(summary.completionRate)}
                    onClick={() => setSel(null)}
                  />
                </div>
                {renderRankedList(
                  getBreakdownList(model, "3"),
                  "Pending backlog",
                )}
                <div className="isf-cc__card">
                  <h3>Pending detail</h3>
                  {filterNote()}
                  {renderDetailTable()}
                </div>
              </section>
              {renderBreakdownView(
                "4",
                "By Staff Role",
                "How each role is performing on assigned learning reads.",
                getBreakdownList(model, "4"),
              )}
              {renderBreakdownView(
                "5",
                "By Staff",
                "How each person is performing on assigned learning reads.",
                getBreakdownList(model, "5"),
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
